import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as clientPOAmendAPI from '../../api/AccountsAPI/clientPOAmendAPI';

// Async Thunks
// =============================================

export const fetchAmendCostCenters = createAsyncThunk(
    'clientPOAmend/fetchAmendCostCenters',
    async (_, { rejectWithValue }) => {
        try {
            const response = await clientPOAmendAPI.getAmendCostCenters();
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Centers');
        }
    }
);

export const fetchPOByCC = createAsyncThunk(
    'clientPOAmend/fetchPOByCC',
    async (ccCode, { rejectWithValue }) => {
        try {
            const response = await clientPOAmendAPI.getPOByCC(ccCode);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch PO Numbers');
        }
    }
);

export const fetchCCDetailsByCcCode = createAsyncThunk(
    'clientPOAmend/fetchCCDetailsByCcCode',
    async (ccCode, { rejectWithValue }) => {
        try {
            const response = await clientPOAmendAPI.getCCDetailsByCcCode(ccCode);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Center Details');
        }
    }
);

export const saveAmendPO = createAsyncThunk(
    'clientPOAmend/saveAmendPO',
    async (params, { rejectWithValue }) => {
        try {
            const response = await clientPOAmendAPI.saveAmendPO(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to save Amend PO');
        }
    }
);

// Initial State
// =============
const initialState = {
    costCenters: [],
    poNumbers: [],
    ccDetails: null,
    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        costCenters: false,
        poNumbers: false,
        ccDetails: false,
        save: false,
    },

    errors: {
        costCenters: null,
        poNumbers: null,
        ccDetails: null,
        save: null,
    },
};

const clientPOAmendSlice = createSlice({
    name: 'clientPOAmend',
    initialState,
    reducers: {
        clearPONumbers: (state) => {
            state.poNumbers = [];
            state.errors.poNumbers = null;
        },
        clearCCDetails: (state) => {
            state.ccDetails = null;
            state.errors.ccDetails = null;
        },
        clearSaveResult: (state) => {
            state.saveResult = null;
            state.saveStatus = null;
            state.errors.save = null;
        },
        resetClientPOAmend: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAmendCostCenters.pending, (state) => {
                state.loading.costCenters = true;
                state.errors.costCenters = null;
            })
            .addCase(fetchAmendCostCenters.fulfilled, (state, action) => {
                state.loading.costCenters = false;
                state.costCenters = action.payload?.Data || [];
            })
            .addCase(fetchAmendCostCenters.rejected, (state, action) => {
                state.loading.costCenters = false;
                state.errors.costCenters = action.payload;
                state.costCenters = [];
            });

        builder
            .addCase(fetchPOByCC.pending, (state) => {
                state.loading.poNumbers = true;
                state.errors.poNumbers = null;
            })
            .addCase(fetchPOByCC.fulfilled, (state, action) => {
                state.loading.poNumbers = false;
                state.poNumbers = action.payload?.Data || [];
            })
            .addCase(fetchPOByCC.rejected, (state, action) => {
                state.loading.poNumbers = false;
                state.errors.poNumbers = action.payload;
                state.poNumbers = [];
            });

        builder
            .addCase(fetchCCDetailsByCcCode.pending, (state) => {
                state.loading.ccDetails = true;
                state.errors.ccDetails = null;
            })
            .addCase(fetchCCDetailsByCcCode.fulfilled, (state, action) => {
                state.loading.ccDetails = false;
                const data = action.payload?.Data;
                state.ccDetails = Array.isArray(data) ? (data[0] || null) : (data || null);
            })
            .addCase(fetchCCDetailsByCcCode.rejected, (state, action) => {
                state.loading.ccDetails = false;
                state.errors.ccDetails = action.payload;
                state.ccDetails = null;
            });

        builder
            .addCase(saveAmendPO.pending, (state) => {
                state.loading.save = true;
                state.errors.save = null;
                state.saveStatus = 'pending';
            })
            .addCase(saveAmendPO.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;

                const responseStr = typeof action.payload?.Data === 'string'
                    ? action.payload.Data
                    : (typeof action.payload === 'string' ? action.payload : '');

                if (responseStr === 'Submited') {
                    state.saveStatus = 'success';
                } else {
                    state.saveStatus = 'failed';
                    state.errors.save = responseStr || 'Save failed';
                }
            })
            .addCase(saveAmendPO.rejected, (state, action) => {
                state.loading.save = false;
                state.errors.save = action.payload;
                state.saveStatus = 'failed';
                state.saveResult = null;
            });
    },
});

export const {
    clearPONumbers,
    clearCCDetails,
    clearSaveResult,
    resetClientPOAmend,
} = clientPOAmendSlice.actions;

// Selectors
// =========
export const selectCostCentersArray = (state) => {
    const cc = state.clientPOAmend.costCenters;
    return Array.isArray(cc) ? cc : [];
};
export const selectCostCentersLoading = (state) => state.clientPOAmend.loading.costCenters;

export const selectPONumbersArray = (state) => {
    const po = state.clientPOAmend.poNumbers;
    return Array.isArray(po) ? po : [];
};
export const selectPONumbersLoading = (state) => state.clientPOAmend.loading.poNumbers;

export const selectCCDetails = (state) => state.clientPOAmend.ccDetails;
export const selectCCDetailsLoading = (state) => state.clientPOAmend.loading.ccDetails;

export const selectSaveStatus = (state) => state.clientPOAmend.saveStatus;
export const selectSaveLoading = (state) => state.clientPOAmend.loading.save;
export const selectSaveError = (state) => state.clientPOAmend.errors.save;

export default clientPOAmendSlice.reducer;
